import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import adminAPI from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { toast } from 'sonner';
import { Search, ChevronLeft, ChevronRight, MoreHorizontal, Eye, Ban, Trash2, CheckCircle } from 'lucide-react';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionMenu, setActionMenu] = useState(null);
  const { admin } = useAuth();
  const navigate = useNavigate();

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      if (status) params.status = status;
      const data = await adminAPI.getUsers(params);
      setUsers(data.users || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleDisable = async (userId) => {
    try {
      await adminAPI.disableUser(userId);
      toast.success('Bruker deaktivert');
      loadUsers();
    } catch (err) { toast.error(err.message); }
    setActionMenu(null);
  };

  const handleEnable = async (userId) => {
    try {
      await adminAPI.enableUser(userId);
      toast.success('Bruker aktivert');
      loadUsers();
    } catch (err) { toast.error(err.message); }
    setActionMenu(null);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne brukeren? All data blir slettet permanent.')) return;
    try {
      await adminAPI.deleteUser(userId);
      toast.success('Bruker slettet');
      loadUsers();
    } catch (err) { toast.error(err.message); }
    setActionMenu(null);
  };

  return (
    <div>
      <div className="mb-6 sm:mb-8">
        <h1 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl text-white">Brukere</h1>
        <p className="text-xs sm:text-sm text-white/40 mt-1">{total} brukere totalt</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4 sm:mb-6">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            data-testid="admin-users-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søk etter navn eller e-post..."
            className="w-full h-10 sm:h-11 pl-10 sm:pl-11 pr-4 rounded-[12px] sm:rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/35 transition-[box-shadow] duration-180"
          />
        </div>
        <select
          data-testid="admin-users-status-filter"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="h-11 px-4 rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/35"
        >
          <option value="">Alle statuser</option>
          <option value="active">Aktiv</option>
          <option value="disabled">Deaktivert</option>
        </select>
      </div>

      {/* Table - Desktop / Card list - Mobile */}
      <div data-testid="admin-users-table" className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" /></div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-sm text-white/30">Ingen brukere funnet</div>
        ) : (
          <>
            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-white/[0.03]">
              {users.map((u) => (
                <div key={u.id} className="p-4 relative">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0" onClick={() => navigate(`/admin/users/${u.id}`)} style={{ cursor: 'pointer' }}>
                      <p className="text-sm text-white font-medium truncate">{u.name || '—'}</p>
                      <p className="text-xs text-white/40 truncate mt-0.5">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-medium ${
                        u.onboarding_complete ? 'bg-[#34C759]/15 text-[#34C759]' : 'bg-white/5 text-white/30'
                      }`}>
                        {u.onboarding_complete ? 'Onboardet' : 'Ikke onboardet'}
                      </span>
                      <button
                        data-testid="admin-users-row-actions-menu"
                        onClick={() => setActionMenu(actionMenu === u.id ? null : u.id)}
                        className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/5"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {u.created_at && <p className="text-[10px] text-white/20 mt-1.5">{new Date(u.created_at).toLocaleDateString('nb-NO')}</p>}
                  {actionMenu === u.id && (
                    <div className="absolute right-4 top-14 z-50 w-40 rounded-[12px] bg-[#121212] border border-white/10 py-1 shadow-xl">
                      <button onClick={() => { navigate(`/admin/users/${u.id}`); setActionMenu(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:bg-white/5">
                        <Eye className="w-3.5 h-3.5" /> Vis detaljer
                      </button>
                      {u.status === 'disabled' ? (
                        <button onClick={() => handleEnable(u.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#34C759] hover:bg-white/5">
                          <CheckCircle className="w-3.5 h-3.5" /> Aktiver
                        </button>
                      ) : (
                        <button onClick={() => handleDisable(u.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#FF9F0A] hover:bg-white/5">
                          <Ban className="w-3.5 h-3.5" /> Deaktiver
                        </button>
                      )}
                      {admin?.role === 'superadmin' && (
                        <button onClick={() => handleDelete(u.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#FF3B30] hover:bg-white/5">
                          <Trash2 className="w-3.5 h-3.5" /> Slett
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] font-medium text-white/30">Navn</th>
                    <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] font-medium text-white/30">E-post</th>
                    <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] font-medium text-white/30">Telefon</th>
                    <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] font-medium text-white/30">Onboardet</th>
                    <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] font-medium text-white/30">Registrert</th>
                    <th className="text-right px-5 py-3 uppercase tracking-[0.18em] text-[10px] font-medium text-white/30">Handlinger</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-[background-color] duration-180">
                      <td className="px-5 py-3.5">
                        <span className="text-sm text-white font-medium">{u.name || '—'}</span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-white/60">{u.email}</td>
                      <td className="px-5 py-3.5 text-sm text-white/60">{u.phone || '—'}</td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium ${
                          u.onboarding_complete ? 'bg-[#34C759]/15 text-[#34C759]' : 'bg-white/5 text-white/40'
                        }`}>
                          {u.onboarding_complete ? 'Ja' : 'Nei'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-white/40">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('nb-NO') : '—'}
                      </td>
                      <td className="px-5 py-3.5 text-right relative">
                        <button
                          data-testid="admin-users-row-actions-menu"
                          onClick={() => setActionMenu(actionMenu === u.id ? null : u.id)}
                          className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/5 transition-[background-color,color] duration-180"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                        {actionMenu === u.id && (
                          <div className="absolute right-5 top-12 z-50 w-44 rounded-[12px] bg-[#121212] border border-white/10 py-1 shadow-xl">
                            <button onClick={() => { navigate(`/admin/users/${u.id}`); setActionMenu(null); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-white/70 hover:bg-white/5">
                              <Eye className="w-3.5 h-3.5" /> Vis detaljer
                            </button>
                            {u.status === 'disabled' ? (
                              <button onClick={() => handleEnable(u.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#34C759] hover:bg-white/5">
                                <CheckCircle className="w-3.5 h-3.5" /> Aktiver
                              </button>
                            ) : (
                              <button onClick={() => handleDisable(u.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#FF9F0A] hover:bg-white/5">
                                <Ban className="w-3.5 h-3.5" /> Deaktiver
                              </button>
                            )}
                            {admin?.role === 'superadmin' && (
                              <button onClick={() => handleDelete(u.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#FF3B30] hover:bg-white/5">
                                <Trash2 className="w-3.5 h-3.5" /> Slett
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-white/5">
            <p className="text-xs text-white/30">Side {page} av {pages}</p>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 disabled:opacity-20 transition-[background-color,color,opacity] duration-180">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 disabled:opacity-20 transition-[background-color,color,opacity] duration-180">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
